import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBillingDetails, performBillingAction } from '../store/slices/billingSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export const useBilling = () => {
  const dispatch = useDispatch();
  const {
    billings,
    metrics,
    loading,
    error,
    pagination
  } = useSelector(state => state.billing);

  const fetchBillings = useCallback((params) => {
    const apiParams = {
      page: params.page,
      per_page: params.perPage || 10,
      status: params.status === 'all' ? undefined : params.status,
      date_range: params.dateRange,
      search: params.search,
      sort_by: params.sortBy,
      sort_order: params.sortOrder
    };
    return dispatch(fetchBillingDetails(apiParams));
  }, [dispatch]);

  const handleAction = useCallback(async (loadId, action, additionalData = {}) => {
    try {
      console.log('handleAction called with:', { loadId, action, additionalData });
      
      const billing = billings.find(b => b.id === loadId);
      if (!billing) {
        throw new Error('No billing record found for load: ' + loadId);
      }

      // Use transaction_uuid if available, fall back to transaction_id for backwards compatibility
      const transactionIdentifier = billing.transaction_uuid || billing.transaction_id;
      if (!transactionIdentifier) {
        throw new Error('No transaction identifier found for load: ' + loadId);
      }

      const apiData = {
        action,
        ...(action === 'dispute_charges' && {
          reason: additionalData.reason || 'Dispute initiated',
          details: additionalData.details || ''
        }),
        ...(action !== 'dispute_charges' && additionalData)
      };

      console.log('Sending API request:', {
        transactionId: transactionIdentifier,
        data: apiData
      });

      const result = await dispatch(performBillingAction({ 
        transactionId: transactionIdentifier,
        action,
        data: apiData
      })).unwrap();

      await dispatch(fetchBillingDetails({
        page: 1,
        status: 'all'
      }));

      toast.success('Billing action completed successfully', {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });

      return true;
    } catch (error) {
      const errorMessage = error.message || 'Action failed';
      const allowedActions = error.allowedActions || [];
      
      toast.error(
        `${errorMessage}${allowedActions.length ? `\nAllowed actions: ${allowedActions.join(', ')}` : ''}`, 
        {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        }
      );
      
      console.error('Action failed:', error);
      throw error;
    }
  }, [dispatch, billings]);

  const getNextActions = useCallback((billing) => {
    if (billing.valid_transitions) {
      return billing.valid_transitions;
    }

    const statusMap = {
      'load_completed': ['invoice_uploaded'],
      'invoice_uploaded': ['charges_approved', 'charges_disputed'],
      'charges_approved': ['awaiting_payment'],
      'charges_disputed': ['dispute_resolved'],
      'awaiting_payment': ['mark_payment'],
      'payment_marked': ['payment_verified'],
      'payment_verified': ['released_to_carrier'],
      'payment_received': ['released_to_carrier']
    };

    return statusMap[billing.status] || [];
  }, []);

  const getTimelineProgress = useCallback((status) => {
    const normalPath = [
      'load_completed',
      'invoice_uploaded',
      'charges_approved',
      'awaiting_payment',
      'payment_marked',
      'payment_verified',
      'released_to_carrier'
    ];
    
    const disputePath = [
      'load_completed',
      'invoice_uploaded',
      'charges_disputed',
      'under_review',
      'charges_approved',
      'awaiting_payment',
      'payment_marked',
      'payment_verified',
      'released_to_carrier'
    ];

    const isDisputed = status === 'charges_disputed' || status === 'under_review';
    const path = isDisputed ? disputePath : normalPath;
    
    return {
      currentIndex: path.indexOf(status),
      totalSteps: path.length,
      path
    };
  }, []);

  return {
    billings,
    metrics,
    loading,
    error,
    pagination,
    fetchBillings,
    handleAction,
    getNextActions,
    getTimelineProgress
  };
}; 